import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { HorseModalProvider, useHorseModal } from './context/HorseModalContext';
import { BusinessProvider } from './context/BusinessContext';
import { UsefulLinksProvider } from './context/UsefulLinksContext';
import HorseModal from './components/HorseModal';

// Pages
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import HorsesPage from './pages/HorsesPage';
import HorseDetailPage from './pages/HorseDetailPage';
import BreedingPage from './pages/BreedingPage';
import BreedingDetailPage from './pages/BreedingDetailPage';
import HypotheticalPedigreePage from './pages/HypotheticalPedigreePage';
import MareOwnersPage from './pages/MareOwnersPage';
import AppointmentsPage from './pages/AppointmentsPage';
import AppointmentTypesPage from './pages/AppointmentTypesPage';
import VetsPage from './pages/VetsPage';
import TrainersPage from './pages/TrainersPage';
import FeedPage from './pages/FeedPage';
import InventoryPage from './pages/InventoryPage';
import InventoryCountPage from './pages/InventoryCountPage';
import TimeClockPage from './pages/TimeClockPage';
import ExpensesPage from './pages/ExpensesPage';
import IncomesPage from './pages/IncomesPage';
import BusinessPage from './pages/BusinessPage';
import SystemStatusPage from './pages/SystemStatusPage';
import ShowsPage from './pages/ShowsPage';
import ReportsPage from './pages/ReportsPage';
import ExpenseSummaryReportPage from './pages/ExpenseSummaryReportPage';
import AdminExpenseTypesPage from './pages/AdminExpenseTypesPage';
import AdminExpenseLocationsPage from './pages/AdminExpenseLocationsPage';
import AdminMileageRatesPage from './pages/AdminMileageRatesPage';
import AdminPersonalInboxFiltersPage from './pages/AdminPersonalInboxFiltersPage';
import AdminIncomeTypesPage from './pages/AdminIncomeTypesPage';
import AdminIncomeLocationsPage from './pages/AdminIncomeLocationsPage';
import AdminHorsePedigreesPage from './pages/AdminHorsePedigreesPage';
import AdminUsersPage from './pages/AdminUsersPage';
import RanchAssistantPage from './pages/RanchAssistantPage';
import AllBreedSubmissionsPage from './pages/AllBreedSubmissionsPage';
import AdminInventoryPage from './pages/AdminInventoryPage';
import AdminAuditLogPage from './pages/AdminAuditLogPage';
import AdminTimeClockPage from './pages/AdminTimeClockPage';
import WaitlistPage from './pages/WaitlistPage';
import UsefulLinksPage from './pages/UsefulLinksPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import SharedPedigreePage from './pages/SharedPedigreePage';

// Layout
import Layout from './components/Layout/Layout';

import { canManage, COUNTER_HOME, COUNTER_PATHS, isInventoryCounter } from './utils/roles';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (isInventoryCounter(user) && !COUNTER_PATHS.includes(location.pathname)) {
    return <Navigate to={COUNTER_HOME} replace />;
  }

  return children;
};

// Wraps a route only admin and manager may open. The sidebar already hides
// these, so this catches a typed URL or a stale bookmark: send them to the
// dashboard rather than to a page that can only render 403s.
const ManagerRoute = ({ children }) => {
  const { user } = useAuth();

  return canManage(user) ? children : <Navigate to="/" replace />;
};

// Component to render HorseModal inside the context
// Renders all modals in the stack with proper z-index layering
const HorseModalWrapper = () => {
  const { modalStack, closeHorseModal } = useHorseModal();

  return (
    <>
      {modalStack.map((modal, index) => {
        const handleSuccess = (newHorse) => {
          if (modal.onSuccessCallback) {
            modal.onSuccessCallback(newHorse);
          }
          // Close only this modal, not all modals
          closeHorseModal();
        };

        return (
          <HorseModal
            key={modal.id}
            open={true}
            onClose={closeHorseModal}
            horse={modal.horse}
            onSuccess={handleSuccess}
            // Increase z-index for nested modals
            // Start at 1300 (Material-UI default) and increase by 20 for each nested modal
            // Use larger increments to avoid conflicts with nested reference modals (z-index 1400)
            // First modal at 1300, each nested modal +20
            zIndex={1300 + index * 20}
          />
        );
      })}
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <BusinessProvider>
        <UsefulLinksProvider>
        <HorseModalProvider>
          <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsConditionsPage />} />
            {/* Public share link — no auth, no Layout chrome. The token is
                the only access control. */}
            <Route path="/shared-pedigree/:token" element={<SharedPedigreePage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="horses" element={<HorsesPage />} />
              <Route path="horses/:id" element={<HorseDetailPage />} />
              <Route path="breeding" element={<BreedingPage />} />
              <Route path="breeding/:id" element={<BreedingDetailPage />} />
              <Route path="mare-owners" element={<MareOwnersPage />} />
              <Route path="appointments" element={<AppointmentsPage />} />
              <Route path="appointment-types" element={<AppointmentTypesPage />} />
              <Route path="vets" element={<VetsPage />} />
              <Route path="trainers" element={<TrainersPage />} />
              <Route path="feed" element={<FeedPage />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="inventory/count" element={<InventoryCountPage />} />
              <Route path="time-clock" element={<TimeClockPage />} />
              <Route path="expenses" element={<ExpensesPage />} />
              <Route path="income" element={<IncomesPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="reports/expense-summary" element={<ExpenseSummaryReportPage />} />
              <Route path="shows" element={<ShowsPage />} />
              <Route path="waitlist" element={<ManagerRoute><WaitlistPage /></ManagerRoute>} />
              <Route path="assistant" element={<RanchAssistantPage />} />
              <Route path="genetics/hypothetical-pedigree" element={<HypotheticalPedigreePage />} />
              <Route path="admin/business" element={<BusinessPage />} />
              <Route path="admin/users" element={<AdminUsersPage />} />
              <Route path="admin/system-status" element={<SystemStatusPage />} />
              <Route path="admin/expense-types" element={<AdminExpenseTypesPage />} />
              <Route path="admin/expense-locations" element={<AdminExpenseLocationsPage />} />
              <Route path="admin/mileage-rates" element={<AdminMileageRatesPage />} />
              <Route path="admin/personal-inbox-filters" element={<AdminPersonalInboxFiltersPage />} />
              <Route path="admin/income-types" element={<AdminIncomeTypesPage />} />
              <Route path="admin/income-locations" element={<AdminIncomeLocationsPage />} />
              <Route path="admin/inventory" element={<AdminInventoryPage />} />
              <Route path="admin/time-clock" element={<AdminTimeClockPage />} />
              <Route path="admin/horse-pedigrees" element={<AdminHorsePedigreesPage />} />
              <Route path="admin/allbreed" element={<AllBreedSubmissionsPage />} />
              <Route path="admin/audit-log" element={<AdminAuditLogPage />} />
              {/* The waitlist left the Admin menu; keep old bookmarks alive. */}
              <Route path="admin/waitlist" element={<Navigate to="/waitlist" replace />} />
              <Route path="admin/useful-links" element={<UsefulLinksPage />} />
            </Route>
          </Routes>
        </Router>
        <HorseModalWrapper />
      </HorseModalProvider>
        </UsefulLinksProvider>
      </BusinessProvider>
    </AuthProvider>
  );
}

export default App;

